import { history, useIntl, useModel } from "@umijs/max";
import styles from "./index.less";
import { Divider, message, Row, Tooltip } from "antd";
import { ResponseCode, routerPath, TOKEN } from "@/constants";
import { logoutService } from "@/services/user";
import { ReactComponent as PoweroffIcon } from "@/assets/images/poweroff.svg";
import { ReactNode, useState } from "react";
import { ReactComponent as ArrowIcon } from "@/assets/images/arrow.svg";
import { ReactComponent as UserIcon } from "@/assets/images/user.svg";
import { backOffical } from "@/utils/backOffical";
import { getSecondLevelDomain } from "@/utils";
import Cookies from "js-cookie";
import { damselfishPersistentStoreActions } from "@/store";
import { wechatChatPersistentStoreActions } from "@/pages/channel-chat/channel-chat-list/wechat-chat/store";
import { wecomChatPersistentStoreActions } from "@/pages/channel-chat/channel-chat-list/wecom-chat/store";
import { wechatContactsPersistentStoreActions } from "@/pages/channel-chat/channel-customer-list/wechat-contacts/store";
import { wecomContactsPersistentStoreActions } from "@/pages/channel-chat/channel-customer-list/wecom-contacts/store";
import UserPlanResourceCard from "@packages/pricing-components/src/components/UserPlanResourceCard";
import {
  isPredefinedVersion,
  PlanPayModalTypeEnum,
  PricingPeriodEnum,
  PricingPlanVersionEnum,
} from "@packages/pricing-components";
import BuyOrRenewSeatModal from "../TradingSystem/BuyOrRenewSeatModal";
import BuyPowerModal from "../TradingSystem/BuyPowerModal";
import Avatar from "../Avatar";
import PlanPayModal from "@packages/pricing-components/src/modals/PlanPayModal";
import {
  getCustomPlanService,
  PayType,
} from "@packages/pricing-components/src/service";
import { useRequest } from "ahooks";
import { transformPlanResponseToVersionWithPriceMap } from "@packages/pricing-components/src/utils";

type MenuItemProps = {
  icon?: ReactNode | null;
  title: string;
  arrow?: boolean;
  onClick?: () => void;
};

const MenuItem = (props: MenuItemProps) => {
  const { icon, title, arrow = true, onClick } = props;
  return (
    <Row
      className={styles["menu-item"]}
      onClick={onClick}
      align="middle"
      justify="space-between"
    >
      <Row justify="space-between">
        <div className="flex items-center">
          {icon && <span className="mr-[6px]">{icon}</span>}
          {title}
        </div>
      </Row>
      {arrow && <ArrowIcon />}
    </Row>
  );
};

interface UserCardProps {
  onClose: () => void;
}

export default function UserCard(props: UserCardProps) {
  const { onClose } = props;

  const { initialState } = useModel("@@initialState");
  const userPlanSubscriptionInformation =
    initialState?.userPlanSubscriptionInformation;
  const userResourceInfo = initialState?.userResourceInformation;

  const intl = useIntl();

  const [openBuyPowerModal, setOpenBuyPowerModal] = useState<boolean>(false);
  const [openBuySeatModal, setOpenBuySeatModal] = useState<boolean>(false);

  const [openPayModal, setOpenPayModal] = useState<boolean>(false);

  const { data: customPlanMap } = useRequest(
    async () => {
      const rsp = await getCustomPlanService();

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }

      return transformPlanResponseToVersionWithPriceMap(rsp.data)[
        userPlanSubscriptionInformation?.paidVersion ??
          PricingPlanVersionEnum.BASIC
      ];
    },
    {
      ready: !isPredefinedVersion(userPlanSubscriptionInformation?.paidVersion),
    }
  );

  return (
    <>
      <div
        className={styles["dropdown-card"]}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-[4px] flex flex-row justify-start items-center gap-[12px]">
          <Avatar
            className={`${styles.avatar} flex-shrink-0`}
            images={initialState?.userInfo?.user.avatar}
            fallback={initialState?.userInfo?.user?.nickName}
          />

          <div className="flex flex-col justify-start items-start">
            <div className={styles.username}>
              <Tooltip
                title={initialState?.userInfo?.user?.nickName}
                placement="right"
              >
                {initialState?.userInfo?.user?.nickName}
              </Tooltip>
            </div>
            {(initialState?.userInfo?.user.phone ||
              initialState?.userInfo?.user?.email) && (
              <div className={styles.phone}>
                {initialState?.userInfo?.user.phone ||
                  initialState?.userInfo?.user?.email}
              </div>
            )}
          </div>
        </div>

        <div className="mt-[20px] mb-[8px]">
          <UserPlanResourceCard
            expireAt={userPlanSubscriptionInformation?.expireAt}
            showExpireNotice={userPlanSubscriptionInformation?.showExpireNotice}
            paidVersion={
              userPlanSubscriptionInformation?.paidVersion ??
              PricingPlanVersionEnum.BASIC
            }
            trailPlan={userPlanSubscriptionInformation?.trailPlan ?? false}
            availableSeat={userResourceInfo?.seatRemain}
            availableRobot={userResourceInfo?.robotRemain}
            availablePower={userResourceInfo?.powerRemain}
            onBuySeat={() => {
              onClose();
              setOpenBuyPowerModal(false);
              setOpenBuySeatModal(true);
            }}
            onBuyRobot={() => {
              onClose();
              history.push("/pricing");
            }}
            onBuyPower={() => {
              onClose();
              setOpenBuySeatModal(false);
              setOpenBuyPowerModal(true);
            }}
            onRenew={() => {
              onClose();
              history.push("/pricing", {
                action: "renew",
              });
            }}
            onRenewCustomPlan={() => {
              onClose();
              setOpenPayModal(true);
            }}
            onChangeSubscription={() => {
              onClose();
              history.push("/pricing");
            }}
          />
        </div>

        <div>
          <MenuItem
            icon={<UserIcon />}
            title={intl.formatMessage({ id: "menu.profile.account" })}
            onClick={() => window.open(routerPath.USER_CENTER, "_blank")}
          />
        </div>

        <Divider className={styles.divider} />

        <div className={styles.action}>
          <div
            className={styles.logout}
            onClick={async () => {
              const rsp = await logoutService();
              if (rsp.code === ResponseCode.Success) {
                message.success("操作成功");
                Cookies.remove(TOKEN);
                Cookies.remove(TOKEN, { domain: getSecondLevelDomain() });
                damselfishPersistentStoreActions.reset();
                wechatChatPersistentStoreActions.reset();
                wecomChatPersistentStoreActions.reset();
                wechatContactsPersistentStoreActions.reset();
                wecomContactsPersistentStoreActions.reset();
                setTimeout(() => {
                  backOffical();
                }, 1000);
              } else {
                message.error(rsp.msg);
              }
            }}
          >
            <PoweroffIcon style={{ marginRight: 4 }} />
            {intl.formatMessage({ id: "menu.profile.logout" })}
          </div>
        </div>
      </div>

      <BuyPowerModal open={openBuyPowerModal} setOpen={setOpenBuyPowerModal} />
      <BuyOrRenewSeatModal
        type="buy"
        open={openBuySeatModal}
        setOpen={setOpenBuySeatModal}
      />

      <PlanPayModal
        open={openPayModal}
        title={`续费定制套餐 - ${userPlanSubscriptionInformation?.paidVersion}`}
        type={PlanPayModalTypeEnum.Renew}
        allowedMethods={[
          PayType.WECHAT,
          PayType.ALIPAY,
          PayType.OFFLINE,
          PayType.BALANCE,
        ]}
        priceInformation={customPlanMap}
        onClose={() => {
          setOpenPayModal(false);
        }}
        initialValues={{
          period: PricingPeriodEnum.MONTHLY,
          planNum: 1,
          payMethod: PayType.WECHAT,
        }}
      />
    </>
  );
}
